<template>
  <div class="container-fluid py-4">

    <!-- Header -->
    <div class="row mb-4">
      <div class="col-12 d-flex justify-content-between align-items-center">
        <h5 class="text-dark mb-0 fw-bold">My Authorization History</h5>

        <div class="d-flex gap-2">
          <button class="btn btn-outline-secondary" @click="exportToExcel">
            <i class="fas fa-file-export me-1"></i> EXPORT
          </button>

          <router-link to="/authorization-request">
            <button class="btn text-white" style="background-color:#b03a45;">
              <i class="fas fa-plus-circle me-1"></i> NEW REQUEST
            </button>
          </router-link>
        </div>
      </div>
    </div>

    <!-- Stats -->
    <div class="row mb-4">
      <div
        class="col-md-3 mb-3"
        v-for="c in cards"
        :key="c.label"
      >
        <div class="card border-0 shadow-sm">
          <div class="card-body text-center">
            <div class="fs-2 mb-2" :class="c.iconClass">
              <i :class="c.icon"></i>
            </div>
            <h6 class="fw-bold mb-1">{{ c.value }}</h6>
            <p class="text-muted mb-0 small">{{ c.label }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Table -->
    <div class="card shadow-sm border-0 mb-4">
      <div class="card-header bg-burgundy text-white py-3">
        <h6 class="mb-0 fw-bold">My Authorization Requests</h6>
      </div>

      <!-- Filters -->
      <div class="card-body border-bottom">
        <div class="row">
          <div class="col-md-6 mb-2 mb-md-0">
            <select v-model="statusFilter" class="form-select">
              <option value="all">All Status</option>
              <option value="Pending">Pending</option>
              <option value="Approved">Approved</option>
              <option value="Rejected">Rejected</option>
            </select>
          </div>

          <div class="col-md-6">
            <input type="month" v-model="monthFilter" class="form-control" />
          </div>
        </div>
      </div>

      <!-- Table content -->
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-hover m-0">
            <thead class="bg-light">
              <tr>
                <th class="ps-4">DATE</th>
                <th>TIME</th>
                <th>DURATION</th>
                <th>STATUS</th>
                <th>REASON</th>
                <th>SUBMITTED</th>
                <th class="text-center">ACTIONS</th>
              </tr>
            </thead>

            <tbody>
              <tr v-if="loading">
                <td colspan="7" class="text-center py-4">
                  <i class="fas fa-spinner fa-spin me-2"></i> Loading...
                </td>
              </tr>

              <tr v-else-if="filteredRequests.length === 0">
                <td colspan="7" class="text-center py-5 text-muted">
                  <i class="fas fa-schedule mb-2" style="font-size:1.6rem;"></i>
                  <div>No authorization requests found.</div>
                  <router-link
                    to="/authorization-request"
                    class="btn btn-outline-primary btn-sm mt-2"
                  >
                    Create your first request
                  </router-link>
                </td>
              </tr>

              <tr
                v-else
                v-for="r in filteredRequests"
                :key="r.id"
              >
                <td class="ps-4 fw-semibold">{{ formatDate(r.date) }}</td>
                <td>{{ r.start_time }} - {{ r.end_time }}</td>
                <td>{{ r.duration_hours }}h</td>

                <td>
                  <span
                    class="badge rounded-pill"
                    :class="getStatusClass(r.status)"
                  >
                    {{ r.status }}
                  </span>
                </td>

                <td>
                  <div
                    class="text-truncate"
                    style="max-width:220px"
                    :title="r.reason"
                  >
                    {{ r.reason || 'No reason provided' }}
                  </div>
                </td>

                <td class="text-muted small">
                  {{ formatDateTime(r.created_at) }}
                </td>

                <td>
                  <div class="d-flex justify-content-center gap-2">
                    <button
                      v-if="r.status === 'Pending'"
                      class="btn btn-ico btn-outline-primary"
                      @click="editRequest(r)"
                    >
                      <i class="fas fa-edit"></i>
                    </button>

                    <button
                      v-if="r.status === 'Pending'"
                      class="btn btn-ico btn-outline-danger"
                      @click="deleteRequest(r.id)"
                    >
                      <i class="fas fa-trash"></i>
                    </button>

                    <button
                      class="btn btn-ico btn-outline-info"
                      @click="viewDetails(r)"
                    >
                      <i class="fas fa-eye"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Pagination -->
      <div
        class="px-4 py-3 d-flex justify-content-end"
        v-if="pagination.total > 0"
      >
        <button
          class="nav-circle me-2"
          :disabled="pagination.current_page === 1"
          @click="loadPage(pagination.current_page - 1)"
        >
          <i class="fas fa-chevron-left"></i>
        </button>

        <span class="align-self-center mx-3 text-muted small">
          Page {{ pagination.current_page }} of {{ pagination.last_page }}
        </span>

        <button
          class="nav-circle"
          :disabled="pagination.current_page === pagination.last_page"
          @click="loadPage(pagination.current_page + 1)"
        >
          <i class="fas fa-chevron-right"></i>
        </button>
      </div>
    </div>

    <!-- Modal -->
    <div
      class="modal fade show"
      v-if="showModal && selectedRequest"
      style="display:block;background:rgba(0,0,0,.5)"
      @click.self="showModal = false"
    >
      <div class="modal-dialog">
        <div class="modal-content">

          <div class="modal-header bg-burgundy text-white">
            <h5 class="modal-title">Authorization Request Details</h5>
            <button class="btn-close btn-close-white" @click="showModal = false"></button>
          </div>

          <div class="modal-body">
            <div class="row g-3">
              <div class="col-6"><strong>Date:</strong><br>{{ formatDate(selectedRequest.date) }}</div>
              <div class="col-6">
                <strong>Status:</strong><br>
                <span class="badge" :class="getStatusClass(selectedRequest.status)">
                  {{ selectedRequest.status }}
                </span>
              </div>

              <div class="col-6"><strong>Start:</strong><br>{{ selectedRequest.start_time }}</div>
              <div class="col-6"><strong>End:</strong><br>{{ selectedRequest.end_time }}</div>
              <div class="col-6"><strong>Duration:</strong><br>{{ selectedRequest.duration_hours }}h</div>
              <div class="col-6"><strong>Submitted:</strong><br>{{ formatDateTime(selectedRequest.created_at) }}</div>

              <div class="col-12" v-if="selectedRequest.reason">
                <strong>Reason:</strong><br>{{ selectedRequest.reason }}
              </div>
            </div>
          </div>

          <div class="modal-footer" v-if="selectedRequest.status === 'Pending'">
            <button class="btn btn-outline-primary" @click="editRequest(selectedRequest)">
              <i class="fas fa-edit me-1"></i> Edit Request
            </button>
          </div>

        </div>
      </div>
    </div>

  </div>
</template>
<script setup>
import { ref, computed, onMounted } from 'vue'
import { useToast } from 'vue-toastification'
import { useRouter } from 'vue-router'
import axios from '@/api'
import * as XLSX from 'xlsx'

const toast = useToast()
const router = useRouter()

const API_PREFIX = '/employee/authorizations'

const loading = ref(true)
const requests = ref([])
const statusFilter = ref('all')
const monthFilter = ref('')
const selectedRequest = ref(null)
const showModal = ref(false)

const pagination = ref({
  current_page: 1,
  last_page: 1,
  total: 0
})

/* ======================
   STATS
====================== */
const stats = computed(() => {
  return {
    pending: requests.value.filter(r => r.status === 'Pending').length,
    approved: requests.value.filter(r => r.status === 'Approved').length,
    rejected: requests.value.filter(r => r.status === 'Rejected').length,
    total: requests.value.length
  }
})

const cards = computed(() => ([
  { label: 'Pending', value: stats.value.pending, icon: 'fas fa-clock', iconClass: 'text-warning' },
  { label: 'Approved', value: stats.value.approved, icon: 'fas fa-check-circle', iconClass: 'text-success' },
  { label: 'Rejected', value: stats.value.rejected, icon: 'fas fa-times-circle', iconClass: 'text-danger' },
  { label: 'Total', value: stats.value.total, icon: 'fas fa-history', iconClass: 'text-info' }
]))

/* ======================
   INIT
====================== */
onMounted(() => {
  const user = JSON.parse(localStorage.getItem('user') || '{}')

  if (!user?.id) {
    toast.error('❌ Please login first')
    router.push({ name: 'SignIn' })
    return
  }

  fetchMyRequests()
})

/* ======================
   API CALLS
====================== */
async function fetchMyRequests(page = 1) {
  loading.value = true
  try {
    const { data } = await axios.get(`${API_PREFIX}/user`, {
      params: { page }
    })

    requests.value = data.data || []

    pagination.value = {
      current_page: data.current_page ?? 1,
      last_page: data.last_page ?? 1,
      total: data.total ?? requests.value.length
    }

  } catch (e) {
    toast.error('Failed to load authorization requests')
  } finally {
    loading.value = false
  }
}

async function deleteRequest(id) {
  if (!confirm('Are you sure you want to delete this request?')) return

  try {
    await axios.delete(`${API_PREFIX}/${id}`)
    toast.success('Request deleted successfully')
    fetchMyRequests(pagination.value.current_page)
  } catch (e) {
    toast.error(e?.response?.data?.message || 'Delete failed')
  }
}

/* ======================
   UI ACTIONS
====================== */
function editRequest(r) {
  router.push({
    name: 'AuthorizationRequest',
    query: {
      edit: r.id,
      date: r.date,
      start_time: r.start_time,
      end_time: r.end_time,
      reason: r.reason || ''
    }
  })
}

function viewDetails(r) {
  selectedRequest.value = r
  showModal.value = true
}

/* ======================
   FILTERS
====================== */
const filteredRequests = computed(() => {
  return requests.value.filter(r => {
    const statusOK = statusFilter.value === 'all' || r.status === statusFilter.value
    const monthOK = !monthFilter.value || (r.date || '').startsWith(monthFilter.value)
    return statusOK && monthOK
  })
})

/* ======================
   EXPORT
====================== */
function exportToExcel() {
  const rows = filteredRequests.value.map(r => ({
    Date: formatDate(r.date),
    'Start Time': r.start_time,
    'End Time': r.end_time,
    'Duration (hours)': r.duration_hours,
    Status: r.status,
    Reason: r.reason || 'No reason provided',
    'Submitted At': formatDateTime(r.created_at)
  }))

  const ws = XLSX.utils.json_to_sheet(rows)
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'My_Authorizations')
  XLSX.writeFile(wb, 'My_Authorizations.xlsx')
}

/* ======================
   HELPERS
====================== */
function formatDate(d) {
  return d ? new Date(d).toLocaleDateString('en-GB') : '—'
}

function formatDateTime(d) {
  return d ? new Date(d).toLocaleString('en-GB') : '—'
}

function getStatusClass(s) {
  switch (s) {
    case 'Approved': return 'bg-success'
    case 'Rejected': return 'bg-danger'
    case 'Pending': return 'bg-warning'
    default: return 'bg-secondary'
  }
}
</script>
